import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Star, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { HelpHint } from './HelpHint';
import { ScrollX } from './ScrollX';

const WINDOWS: Array<[number, string]> = [
  [7, '7 days'],
  [30, '30 days'],
  [90, '90 days'],
];

/**
 * How each technician is doing, as six honest figures rather than one score.
 *
 * A single 0–100 rating would be easier to read and worse to manage by: it
 * hides which part is weak, and it invites people to optimise the number
 * instead of the work. These six each point at a different, actionable thing —
 * a high decline rate is a rota problem, missing evidence is a billing
 * problem, a wasted-trip rate is a triage problem.
 *
 * Only finished work is counted. Counting open jobs would reward whoever has
 * the most tickets sitting on them.
 */
export function PerformanceTable() {
  const [days, setDays] = useState(30);

  const { data, isPending } = useQuery({
    queryKey: ['technician-performance', days],
    queryFn: () => api.technicianPerformance(days),
  });

  const rows = data?.technicians ?? [];

  return (
    <section className="card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 font-heading text-lg">
          <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Performance
          <HelpHint>
            Finished work only, over the window you pick. Open jobs are not counted — they say
            nothing about the person yet.
          </HelpHint>
        </h3>

        <div className="flex gap-1">
          {WINDOWS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              className={`rounded-lg border px-3 py-1.5 text-sm ${
                days === value
                  ? 'border-primary-500 bg-primary-50 text-primary-800'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isPending && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}

      {data && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No technicians on the roster yet.</p>
      )}

      {data && rows.length > 0 && (
        <ScrollX>
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 font-medium">Technician</th>
                <th className="py-2 text-right font-medium">Finished</th>
                <th className="py-2 text-right font-medium">
                  <span title="Jobs finished per working day, against a target of four">
                    A day
                  </span>
                </th>
                <th className="py-2 text-right font-medium">
                  <span title="Jobs where the fault was actually fixed">Fixed</span>
                </th>
                <th className="py-2 text-right font-medium">
                  <span title="Arrived and found nothing, or could not get in">Wasted trips</span>
                </th>
                <th className="py-2 text-right font-medium">Late</th>
                <th className="py-2 text-right font-medium">
                  <span title="Offered and turned down or never answered">Turned down</span>
                </th>
                <th className="py-2 text-right font-medium">
                  <span title="Visits that ran past two hours on site">Over 2h</span>
                </th>
                <th className="py-2 text-right font-medium">
                  <span title="Median time from acknowledging to finishing">Typical job</span>
                </th>
                <th className="py-2 text-right font-medium">Rating</th>
                <th className="py-2 text-right font-medium">Open now</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {rows.map((row) => {
                const wastedRate = row.completed ? row.wasted / row.completed : 0;
                const atCapacity = row.openNow >= row.maxConcurrent;

                return (
                  <tr key={row.technicianId} className={row.isActive ? '' : 'opacity-60'}>
                    <td className="py-2">
                      <span className="font-medium">{row.name}</span>
                      {!row.isActive && (
                        <span className="ml-2 text-xs text-muted-foreground">off roster</span>
                      )}
                      {/* A completed job with no photo cannot be proved, and
                          work that cannot be proved cannot be defended when a
                          customer disputes the bill. */}
                      {row.missingEvidence > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-danger-50 px-2 py-0.5 text-xs text-danger-700">
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                          {row.missingEvidence} with no photo
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">{row.completed}</td>
                    {/*
                      Against the target, and coloured only when it is missed.
                      Colouring the ones that clear it would make a table where
                      everything is doing fine look like an alarm.
                    */}
                    <td
                      className={`py-2 text-right tabular-nums ${
                        row.completed > 0 && !row.meetingTarget ? 'font-semibold text-accent-700' : ''
                      }`}
                      title={`Target ${row.dailyTarget} a working day`}
                    >
                      {row.perDay}
                    </td>
                    <td className="py-2 text-right tabular-nums">{row.fixed}</td>
                    <td
                      className={`py-2 text-right tabular-nums ${
                        wastedRate > 0.25 ? 'font-semibold text-accent-700' : ''
                      }`}
                    >
                      {row.wasted}
                      {row.completed > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          {Math.round(wastedRate * 100)}%
                        </span>
                      )}
                    </td>
                    <td
                      className={`py-2 text-right tabular-nums ${
                        row.breached > 0 ? 'font-semibold text-danger-700' : ''
                      }`}
                    >
                      {row.breached}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.declined + row.ignored}
                      {row.ignored > 0 && (
                        <span
                          className="ml-1 text-xs text-muted-foreground"
                          title={`${row.ignored} never answered`}
                        >
                          ({row.ignored} silent)
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.overran === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={row.overrunRate !== null && row.overrunRate > 25 ? 'font-semibold text-accent-700' : ''}
                          title={`${row.overran} of ${row.visits} visit(s)`}
                        >
                          {row.overran}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.medianOnJobLabel ?? '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.rating === null ? (
                        '—'
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Star
                            className="h-3 w-3 fill-accent-500 text-accent-500"
                            aria-hidden="true"
                          />
                          {row.rating}
                          <span className="text-xs text-muted-foreground">({row.ratingCount})</span>
                        </span>
                      )}
                    </td>
                    <td
                      className={`py-2 text-right tabular-nums ${
                        atCapacity ? 'font-semibold text-accent-700' : ''
                      }`}
                      title={atCapacity ? 'At their limit — dispatch will skip them' : undefined}
                    >
                      {row.openNow}/{row.maxConcurrent}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollX>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        No overall score on purpose. One number would hide which part is weak, and people manage the
        number rather than the work.
      </p>
    </section>
  );
}
